/**
 * NotificationsPage — parent's acceptance-report inbox (Phase 4 Section 4).
 *
 * Lists pending Approve & Apply requests at the top, then a chronological
 * stream of approved / rejected / auto-launched events. Each row shows:
 *   - WHAT changed (diff summary)
 *   - WHO made the change (Therapist / School / Family helper label)
 *   - WHEN it was created
 *   - WHEN it was launched (if applied)
 *   - The version identifier (proposal id)
 *
 * Privacy-safe: no unrelated PII is surfaced. The diff names the BUTTON
 * label only; deep details live on the dedicated proposal review page
 * (existing flow).
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bell, Check, AlertCircle, ChevronRight, RefreshCw, Loader2, X,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSEO } from "@/lib/seo";

const KIND_META = {
  approval_request: { label: "Approval needed",      tone: "request",  icon: AlertCircle },
  approved_applied: { label: "Approved & applied",   tone: "ok",       icon: Check },
  auto_launched:    { label: "Auto-launched",        tone: "ok",       icon: Check },
  rejected:         { label: "Rejected",             tone: "muted",    icon: X },
  info:             { label: "Update",               tone: "muted",    icon: Bell },
};

function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

export default function NotificationsPage() {
  useSEO({
    title: "Notifications — SonSpeak",
    description: "Acceptance reports for every change to your child's library.",
  });
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  const [marking, setMarking] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.get("/notifications");
      setItems(data?.items || []);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    setMarking(id);
    try {
      await api.post(`/notifications/${id}/read`);
      setItems((arr) => arr.map((i) => (i.id === id ? { ...i, read: true } : i)));
      window.dispatchEvent(new CustomEvent("sonspeak:notifications-refresh"));
    } catch { /* ignore */ }
    setMarking(null);
  };
  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems((arr) => arr.map((i) => ({ ...i, read: true })));
      window.dispatchEvent(new CustomEvent("sonspeak:notifications-refresh"));
    } catch { /* ignore */ }
  };

  const pending = items.filter((i) => i.requires_action);
  const history = items.filter((i) => !i.requires_action);

  return (
    <div className="page-wrap pt-6 pb-24" data-testid="notifications-page">
      <div className="max-w-3xl mx-auto px-4">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
          <h1 className="display text-2xl sm:text-3xl font-extrabold flex items-center gap-2">
            <Bell size={22} aria-hidden /> Notifications
          </h1>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={load}
              className="pill-btn"
              disabled={busy}
              data-testid="notifications-refresh"
              aria-label="Refresh"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            </button>
            <button
              type="button"
              onClick={markAllRead}
              className="pill-btn whitespace-nowrap"
              disabled={busy || items.every((i) => i.read)}
              data-testid="notifications-mark-all-read"
            >
              Mark all read
            </button>
          </div>
        </div>
        <p className="text-sm text-[color:var(--ink-soft)] mb-5">
          A real-time log of every change to your child's library. Nothing
          touches your child's tablet without your one-tap approval — except
          school updates after you grant one-time consent (these still
          appear here for your records).
        </p>

        {error && (
          <div className="brutal-card bg-red-50 p-3 text-sm text-red-700 mb-4" data-testid="notifications-error">
            {error}
          </div>
        )}

        {!busy && items.length === 0 && !error && (
          <div className="brutal-card bg-white p-5 text-sm text-[color:var(--ink)]/60" data-testid="notifications-empty">
            Nothing here yet — when a therapist or school helper suggests a
            change, it'll land here with one tap to approve.
          </div>
        )}

        {pending.length > 0 && (
          <section className="mb-6" data-testid="notifications-pending">
            <h2 className="display font-extrabold text-base mb-2">Waiting for you</h2>
            <ul className="space-y-2">
              {pending.map((n) => (
                <NotificationRow
                  key={n.id}
                  n={n}
                  onMarkRead={markRead}
                  marking={marking === n.id}
                  primary
                />
              ))}
            </ul>
          </section>
        )}

        {history.length > 0 && (
          <section data-testid="notifications-history">
            <h2 className="display font-extrabold text-base mb-2">Recent activity</h2>
            <ul className="space-y-2">
              {history.map((n) => (
                <NotificationRow
                  key={n.id}
                  n={n}
                  onMarkRead={markRead}
                  marking={marking === n.id}
                />
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}

function NotificationRow({ n, onMarkRead, marking, primary }) {
  const meta = KIND_META[n.kind] || KIND_META.info;
  const Icon = meta.icon;
  const toneClass =
    meta.tone === "request" ? "bg-amber-50 border-amber-300"
    : meta.tone === "ok"    ? "bg-[color:var(--cream)]"
    : "bg-white";
  return (
    <li
      className={`brutal-card p-3 sm:p-4 flex items-start gap-3 ${toneClass} ${n.read ? "opacity-80" : ""}`}
      data-testid={`notification-${n.id}`}
    >
      <Icon size={18} className="mt-0.5 shrink-0" aria-hidden />
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className="text-[10px] uppercase tracking-[0.14em] font-extrabold bg-[color:var(--ink)] text-white px-1.5 py-0.5 rounded">
            {meta.label}
          </span>
          <span className="text-xs font-semibold text-[color:var(--ink-soft)]">
            {n.actor_label} · {n.profile_label}
          </span>
          {!n.read && (
            <span
              className="inline-block w-2 h-2 rounded-full bg-[color:var(--coral)]"
              aria-label="Unread"
              data-testid={`notification-${n.id}-unread-dot`}
            />
          )}
        </div>
        <div className="font-bold text-sm sm:text-base mb-1">
          {n.diff?.summary || "Change to your child's library"}
        </div>
        <div className="text-[11px] text-[color:var(--ink-soft)] flex flex-wrap gap-x-3 gap-y-0.5">
          <span>created {fmtTime(n.created_at)}</span>
          {n.launched_at && <span>· launched {fmtTime(n.launched_at)}</span>}
          {n.decided_at && !n.launched_at && <span>· decided {fmtTime(n.decided_at)}</span>}
          <span className="font-mono">· #{(n.version_id || "").slice(0, 8)}</span>
        </div>
      </div>
      <div className="flex flex-col gap-1 shrink-0">
        {primary && (
          <Link
            to={`/tile-proposals/${n.id}`}
            className="pill-btn primary"
            data-testid={`notification-${n.id}-review`}
          >
            Review <ChevronRight size={12} />
          </Link>
        )}
        {!n.read && (
          <button
            type="button"
            onClick={() => onMarkRead(n.id)}
            className="pill-btn text-xs"
            disabled={marking}
            data-testid={`notification-${n.id}-mark-read`}
          >
            {marking ? "…" : "Mark read"}
          </button>
        )}
      </div>
    </li>
  );
}
